import { REGGORA_PANEL_ID, PANEL_SOURCE, HIERARCHY_TYPE } from '../constants/panelSourceConstants'

/**
 * Validates if a string is a valid MongoDB ObjectId
 * ObjectIds are 24-character hexadecimal strings
 * @param {string} str - String to validate
 * @returns {boolean} True if the string is a valid ObjectId
 */
export const isObjectId = (str) => {
  if (typeof str !== 'string') {
    return false
  }
  return /^[0-9a-fA-F]{24}$/.test(str)
}

/**
 * Detects the hierarchy type from the data structure
 * - If entries have branch_id → Non-AMP user (branch hierarchy)
 * - If entries have id (but not branch_id) → AMP user (preferred panel hierarchy)
 * @param {Array} branchHierarchy - Array of hierarchy objects
 * @returns {string} - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL
 */
export const getHierarchyType = (branchHierarchy) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy) || branchHierarchy.length === 0) {
    return HIERARCHY_TYPE.BRANCH
  }

  // Check first item - if it has 'id' field (not 'branch_id'), it's preferred panel
  const firstItem = branchHierarchy[0]
  if ('id' in firstItem && !('branch_id' in firstItem)) {
    return HIERARCHY_TYPE.PREFERRED_PANEL
  }

  return HIERARCHY_TYPE.BRANCH
}

/**
 * Gets the ID from a hierarchy item based on hierarchy type
 * Non-AMP users have branch_id, AMP users have id
 * @param {Object} item - Hierarchy item object
 * @param {string} hierarchyType - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL
 * @returns {string|null} - The ID value
 */
export const getItemId = (item, hierarchyType) => {
  if (hierarchyType === HIERARCHY_TYPE.PREFERRED_PANEL) {
    return item.id
  }
  return item.branch_id
}

/**
 * Extracts IDs from hierarchy, filtering out invalid values
 * Works for both branch hierarchy (branch_id) and preferred panel hierarchy (id)
 * @param {Array} branchHierarchy - Array of hierarchy objects
 * @param {string} hierarchyType - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL
 * @returns {Array} - Array of valid IDs
 */
export const extractHierarchyIds = (branchHierarchy, hierarchyType) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy)) {
    return []
  }

  return branchHierarchy
    .map((item) => getItemId(item, hierarchyType))
    .filter((id) => id !== null && id !== REGGORA_PANEL_ID && id !== undefined)
}

/**
 * Finds the first item in hierarchy with panelists based on aggregation results
 * Traverses from child to parent (first to last in array) until finding an item with panelists
 * Works for both branch hierarchy (branch_id) and preferred panel hierarchy (id)
 * @param {Array} branchHierarchy - Array of hierarchy objects in reverse hierarchical order: [child, parent, grandparent, ...]
 * @param {Object} panelistCounts - Object mapping ID to panelist count
 * @param {string} hierarchyType - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL (optional, auto-detected if not provided)
 * @returns {string|null} - ID with panelists, or null if none found (defaults to Reggora Network)
 */
export const findDefaultBranchWithPanelists = (branchHierarchy, panelistCounts, hierarchyType = null) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy)) {
    return null
  }

  // Auto-detect hierarchy type if not provided
  const type = hierarchyType || getHierarchyType(branchHierarchy)

  for (let i = 0; i < branchHierarchy.length; i++) {
    const item = branchHierarchy[i]
    const itemId = getItemId(item, type)

    // Skip null, undefined, and REGGORA_PANEL_ID
    if (itemId && itemId !== REGGORA_PANEL_ID && itemId !== undefined) {
      const count = panelistCounts[itemId] || 0
      if (count > 0) {
        return itemId
      }
    }
  }

  // No items in hierarchy have panelists, return null to default to Reggora Network
  return null
}

/**
 * Extracts branch IDs from hierarchy, filtering out invalid values
 * @param {Array} branchHierarchy - Array of branch objects
 * @returns {Array} - Array of valid branch IDs
 */
export const extractBranchIds = (branchHierarchy) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy)) {
    return []
  }

  return branchHierarchy
    .map((branch) => branch.branch_id)
    .filter((id) => id !== null && id !== REGGORA_PANEL_ID && id !== undefined)
}

/**
 * Determines panel source filter based on loan and order data
 * @param {Object} selectedLoan - Selected loan object
 * @param {Object} orderData - Order data object
 * @param {boolean} is_amc_lender - Whether user is AMC lender
 * @returns {Object} - Panel source filter object with panelSource property
 */
export const determinePanelSourceFilter = (selectedLoan, orderData, is_amc_lender) => {
  const branchId = selectedLoan?.branch?.id || selectedLoan?.branch_id
  const panelStrict = selectedLoan?.branch?.panel_strict || selectedLoan?.branch_panel_strict
  const preferredPanelId = orderData?.preferred_panel?.id
  const panelSourceFilter = {}

  if (branchId && panelStrict && !is_amc_lender) {
    panelSourceFilter.panelSource = PANEL_SOURCE.BRANCH
  } else if (preferredPanelId && is_amc_lender) {
    panelSourceFilter.panelSource = PANEL_SOURCE.PREFERRED
  }

  return panelSourceFilter
}

/**
 * Gets branch hierarchy from fullSelectedLoan or orderData
 * @param {Object} fullSelectedLoan - Full selected loan object
 * @param {Object} orderData - Order data object
 * @returns {Array|null} - Branch hierarchy array or null
 */
export const getBranchHierarchy = (fullSelectedLoan, orderData) => {
  return (
    fullSelectedLoan?.data?.branch_hierarchy ||
    orderData?.loan_file?.branch_hierarchy ||
    null
  )
}

/**
 * Checks if branch hierarchy is valid for aggregation query
 * @param {Array} branchHierarchy - Branch hierarchy array
 * @returns {boolean} - True if hierarchy is valid
 */
export const isValidBranchHierarchy = (branchHierarchy) => {
  return Boolean(branchHierarchy && Array.isArray(branchHierarchy) && branchHierarchy.length > 0)
}
